import { 
  signInWithPopup, 
  signOut as firebaseSignOut, 
  onAuthStateChanged,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { apiClient } from '@/lib/api-client';
import { SignupRequest } from '@/types/auth';

class AuthService {
  async signInWithGoogle(): Promise<void> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      if (!user) {
        throw new Error('No user returned from Google sign-in');
      }

      // Get the Firebase ID token
      const idToken = await user.getIdToken();
      
      // Prepare signup request
      const signupRequest: SignupRequest = {
        firebase_token: idToken,
        firebase_uid: user.uid,
        display_name: user.displayName || '',
        email: user.email || '',
      };

      const response = await apiClient.post('/auth/signup', signupRequest);
      
      // Set flag to show onboarding survey for new users
      if (response?.isNewUser) {
        localStorage.setItem('show_onboarding_survey', 'true');
      }
      
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user') {
        throw error;
      }
      
      throw error;
    }
  }

  async signUpWithEmail(email: string, password: string, displayName: string): Promise<void> {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      if (!user) {
        throw new Error('No user returned from email sign-up');
      }

      // Update the user's display name
      await updateProfile(user, { displayName });

      // Get the Firebase ID token
      const idToken = await user.getIdToken();
      
      // Prepare signup request
      const signupRequest: SignupRequest = {
        firebase_token: idToken,
        firebase_uid: user.uid,
        display_name: displayName,
        email: email,
      };

      // Call your backend signup endpoint
      const response = await apiClient.post('/auth/signup', signupRequest);
      
      // Set flag to show onboarding survey for new users
      if (response?.isNewUser) {
        localStorage.setItem('show_onboarding_survey', 'true');
      }
      
    } catch (error: any) {
      // Handle specific Firebase auth errors with user-friendly messages
      if (error?.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }
      if (error?.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Please choose a stronger password.');
      }
      if (error?.code === 'auth/invalid-email') {
        throw new Error('Invalid email address. Please enter a valid email.');
      }
      
      throw error;
    }
  }

  async signInWithEmail(email: string, password: string): Promise<void> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      
      if (!user) {
        throw new Error('No user returned from email sign-in');
      }

      // For existing users, no need to call backend - Firebase handles auth state
      
    } catch (error: any) {
      // Handle specific Firebase auth errors with user-friendly messages
      if (error?.code === 'auth/invalid-credential') {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      if (error?.code === 'auth/user-not-found') {
        throw new Error('No account found with this email. Please sign up first.');
      }
      if (error?.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      }
      if (error?.code === 'auth/too-many-requests') {
        throw new Error('Too many failed login attempts. Please try again later.');
      }
      
      throw error;
    }
  }

  async resetPassword(email: string): Promise<void> {
    // Send through our backend (Resend, support@xynth.finance) — Firebase's
    // default sender gets silently dropped by iCloud/Hotmail. Fall back to
    // the Firebase client sender only if the backend is unreachable.
    try {
      const { API_BASE_URL } = await import('@/lib/config');
      const res = await fetch(`${API_BASE_URL}/auth/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) return;
      throw new Error(`password-reset endpoint returned ${res.status}`);
    } catch {
      await sendPasswordResetEmail(auth, email);
    }
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      throw error;
    }
  }

  async getAuthToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;
    
    try {
      return await user.getIdToken();
    } catch (error) {
      return null;
    }
  }

  // Listen for authentication state changes
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
  }
}

export const authService = new AuthService();
